import { useEffect, type ReactElement } from 'react';
import { Box, Group, Loader, Text } from '@mantine/core';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { DashboardTileConfig } from '@/shared/assist/DashboardContract';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { accentRgba, accentTokens } from '@/design/VizAccents';
import type { VizAccent } from '@/design/VizAccents';
import { formatMetricValue, metricLabel } from '@/shared/analytics/MetricFormatting';
import SurfaceAssistChart from '@/shared/surfaces/assist/SurfaceAssistChart';
import MatrixViz from '@/shared/surfaces/MatrixViz';
import StatBand, { type StatItem } from '@/design/StatBand';
import { chartPlanFromTile, definitionFromDashboardTile, tileAccent, tileIcon } from './AIExplorerTileTypes';
import { AIExplorerTileViewModel } from './AIExplorerTileViewModel';
import type { AIExplorerTileData } from './AIExplorerTileQuery';

interface AIExplorerTileProps {
    config: DashboardTileConfig;
    /** The page's live ambient filters, composed with the tile's own. */
    pageFilters: AnalyticsFilter[];
    /** Drill handler for chart bars/slices (the page scopes the dashboard by the clicked value). */
    onDrill: (field: AnalyticsField, value: string) => void;
    /** Base path for matrix cell/row drill links. */
    basePath: string;
    /** Layout/slot-controlled accent that overrides the tile's own (e.g. comparison sides, hero) — also tints the card so a two-tone split reads. */
    accent?: VizAccent;
    /** When 'hero', renders the tile larger/dominant (taller body, bigger title). */
    emphasis?: 'hero';
}

/**
 * Renders one streamed, JSON-configured tile. The config arrives first (so the
 * card + title paint immediately, wiping into its slot); the data then self-loads
 * via {@link AIExplorerTileViewModel} and re-queries when the ambient filters
 * change. Every viz reuses an EXISTING render component — charts via
 * {@link SurfaceAssistChart}, cross-tabs via {@link MatrixViz}, and
 * KPI/stat bands via {@link StatBand}.
 */
export default function AIExplorerTile({ config, pageFilters, onDrill, basePath, accent: accentOverride, emphasis }: AIExplorerTileProps): ReactElement {
    const vm = useViewModel(AIExplorerTileViewModel);
    const accent = accentOverride ?? tileAccent(config);
    const tokens = accentTokens(accent);
    const Icon = tileIcon(config.icon);
    const hero = emphasis === 'hero';
    const tinted = accentOverride !== undefined;

    useEffect(() => {
        void vm.load(config, pageFilters);
    }, [vm, config, pageFilters]);

    return (
        <SurfaceCard
            $padding={hero ? 'lg' : 'md'}
            $borderless
            className="bm-tile-enter"
            style={{ height: '100%', ...(tinted ? { background: accentRgba(accent, 0.06), boxShadow: `inset 4px 0 0 ${tokens.solid}, var(--bm-card-shadow)` } : {}) }}
        >
            <Group justify="space-between" align="flex-start" mb="sm" wrap="nowrap">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Box
                        style={{
                            width: hero ? 32 : 28,
                            height: hero ? 32 : 28,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: tokens.soft,
                            border: `1px solid ${tokens.border}`,
                            borderRadius: 'var(--bm-radius)',
                        }}
                    >
                        <Icon size={hero ? 18 : 15} color={tokens.solid} />
                    </Box>
                    <Box style={{ minWidth: 0 }}>
                        <Text fw={hero ? 800 : 700} truncate style={{ fontSize: hero ? '18px' : '14px', letterSpacing: '-0.02em' }}>
                            {config.title}
                        </Text>
                        {config.subtitle !== undefined && config.subtitle.length > 0 && (
                            <Text size="xs" c="dimmed" truncate>
                                {config.subtitle}
                            </Text>
                        )}
                    </Box>
                </Group>
            </Group>
            <Box style={{ flex: 1, minWidth: 0, ...(hero ? { minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}) }}>
                <DashboardTileBody vm={vm} config={config} onDrill={onDrill} basePath={basePath} accent={accent} />
            </Box>
        </SurfaceCard>
    );
}

interface DashboardTileBodyProps {
    vm: AIExplorerTileViewModel;
    config: DashboardTileConfig;
    onDrill: (field: AnalyticsField, value: string) => void;
    basePath: string;
    accent: ReturnType<typeof tileAccent>;
}

function DashboardTileBody({ vm, config, onDrill, basePath, accent }: DashboardTileBodyProps): ReactElement {
    if (vm.loading && vm.data === null) {
        return (
            <Group justify="center" py="xl">
                <Loader size="sm" />
            </Group>
        );
    }
    if (vm.error !== null) {
        return (
            <Text size="sm" c="red">
                {vm.error}
            </Text>
        );
    }
    if (vm.data === null) return <EmptyBody />;
    return renderBody(vm.data, config, onDrill, basePath, accent);
}

function renderBody(data: AIExplorerTileData, config: DashboardTileConfig, onDrill: (field: AnalyticsField, value: string) => void, basePath: string, accent: ReturnType<typeof tileAccent>): ReactElement {
    if (data.kind === 'stat') {
        const stats: StatItem[] = data.metrics.map((metric) => ({ label: metricLabel(metric), value: formatMetricValue(metric, data.totals[metric]) }));
        return <StatBand stats={stats} />;
    }
    if (data.kind === 'matrix') {
        return <MatrixViz definition={definitionFromDashboardTile(config)} primary={data.primary} matrix={data.matrix} basePath={basePath} />;
    }
    return <SurfaceAssistChart plan={chartPlanFromTile(config)} aggregate={data.aggregate} trend={data.trend} onDrill={onDrill} accent={accent} />;
}

function EmptyBody(): ReactElement {
    return (
        <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: 'var(--mantine-spacing-lg)' }}>
            <Text size="sm" c="dimmed" ta="center">
                No matching activity in the current view.
            </Text>
        </Box>
    );
}
